// src/hooks/unified/useSystemQueries.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import { env } from '../../config/env';
import type { ListFilesParams, DeleteFilesRequest } from '../../services/unified/systemModule';

/**
 * Query keys for system settings
 */
export const systemKeys = {
  all: ['system'] as const,
  settings: () => [...systemKeys.all, 'settings'] as const,
  publicSettings: () => [...systemKeys.all, 'public'] as const,
  files: (params?: ListFilesParams) => [...systemKeys.all, 'files', params] as const,
  storageStats: () => [...systemKeys.all, 'storage-stats'] as const,
};

/**
 * Hook to fetch public system settings (no auth required)
 */
export function usePublicSystemSettings() {
  return useQuery({
    queryKey: systemKeys.publicSettings(),
    queryFn: async () => {
      return await unifiedApiService.system.getPublicSettings();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - public settings change rarely
    retry: 1, // Only retry once on failure
    retryDelay: 1000, // Wait 1 second before retrying
  });
}

/**
 * Hook to get public settings as a simple key-value map
 */
export function usePublicSettingsMap() {
  return useQuery({
    queryKey: [...systemKeys.publicSettings(), 'map'],
    queryFn: async () => {
      return await unifiedApiService.system.getPublicSettingsMap();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to get a specific public setting value
 */
export function usePublicSetting(key: string) {
  return useQuery({
    queryKey: [...systemKeys.publicSettings(), key],
    queryFn: async () => {
      return await unifiedApiService.system.getPublicSetting(key);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to get company contact information
 */
export function useCompanyContact() {
  const { data: settings } = usePublicSystemSettings();

  return {
    address: settings?.address?.value,
    phone: settings?.phone?.value,
    email: settings?.email?.value,
  };
}

/**
 * Hook to get company social media links
 */
export function useCompanySocial() {
  const { data: settings } = usePublicSystemSettings();

  return {
    linkedin: settings?.social_linkedin?.value,
    x: settings?.social_x?.value,
    instagram: settings?.social_instagram?.value,
    facebook: settings?.social_facebook?.value,
  };
}

/**
 * Hook to get company branding information
 */
export function useCompanyBranding() {
  const { data: settings, isError } = usePublicSystemSettings();

  // Helper to get full URL
  const getFullUrl = (url: string | undefined): string | undefined => {
    if (!url) return undefined;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${env.API_BASE_URL}${url}`;
  };

  // If there's an error fetching settings, return defaults
  if (isError) {
    return {
      name: 'Career Plus',
      tagline: undefined,
      description: undefined,
      logo: undefined,
    };
  }

  return {
    name: settings?.application_name?.value || 'Career Plus',
    tagline: settings?.tagline?.value,
    description: settings?.company_description?.value,
    logo: getFullUrl(settings?.company_logo?.value),
  };
}

/**
 * Hook for file management queries and mutations
 */
export function useSystemQueries() {
  const queryClient = useQueryClient();

  return {
    // List files query
    listFiles: {
      useQuery: (params?: ListFilesParams) =>
        useQuery({
          queryKey: systemKeys.files(params),
          queryFn: async () => {
            return await unifiedApiService.system.listFiles(params);
          },
          staleTime: 30 * 1000, // 30 seconds
        }),
    },

    // Storage stats query
    getStorageStats: {
      useQuery: () =>
        useQuery({
          queryKey: systemKeys.storageStats(),
          queryFn: async () => {
            return await unifiedApiService.system.getStorageStats();
          },
          staleTime: 60 * 1000, // 1 minute
        }),
    },

    // Delete single file mutation
    deleteFile: useMutation({
      mutationFn: async (path: string) => {
        return await unifiedApiService.system.deleteFile(path);
      },
      onSuccess: () => {
        // Invalidate files and stats queries
        queryClient.invalidateQueries({ queryKey: systemKeys.all });
      },
    }),

    // Delete multiple files mutation
    deleteFiles: useMutation({
      mutationFn: async (request: DeleteFilesRequest) => {
        return await unifiedApiService.system.deleteFiles(request);
      },
      onSuccess: () => {
        // Invalidate files and stats queries
        queryClient.invalidateQueries({ queryKey: systemKeys.all });
      },
    }),
  };
}
